import React from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import type { DialOpAgentRow } from '../../../types/dialingOpportunity';
import {
  buildDialOpGridSx,
  DIAL_OP_PAGE_SIZE_OPTIONS,
  DialOpGridToolbar,
  HeaderWithTooltip,
} from './dialOpTableKit';

interface AgentViewTableProps {
  rows: DialOpAgentRow[];
  /** Agents currently active in the global filter -- can be more than one. */
  selectedAgents?: string[];
  /** Toggles a single agent in/out of the global filter's selection. */
  onAgentToggle?: (agent: string) => void;
  /** Render without the outer Paper/header chrome (for use inside a section). */
  bare?: boolean;
}

export default function AgentViewTable({
  rows,
  selectedAgents,
  onAgentToggle,
  bare = false,
}: AgentViewTableProps) {
  const theme = useTheme();

  const gridRows = React.useMemo(
    () => rows.map((row, idx) => ({ id: row.agent_full_nm ?? `agent-${idx}`, ...row })),
    [rows],
  );

  const clickable = Boolean(onAgentToggle);

  const columns = React.useMemo<GridColDef[]>(
    () => [
      { field: 'agent_full_nm', headerName: 'Agent', minWidth: 160, flex: 1.2 },
      { field: 'agent_title', headerName: 'Title', minWidth: 160, flex: 1 },
      {
        field: 'dials',
        headerName: 'Dials',
        type: 'number',
        minWidth: 88,
        flex: 0.7,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | null) => (value ?? 0).toLocaleString(),
      },
      {
        field: 'connect_rate_pct',
        headerName: 'Connect %',
        type: 'number',
        minWidth: 100,
        flex: 0.8,
        align: 'right',
        headerAlign: 'right',
        renderHeader: () => (
          <HeaderWithTooltip label="Connect %" tooltip="Share of first-call dials reaching 90+ seconds." />
        ),
        valueFormatter: (value: number | null) => `${(value ?? 0).toFixed(1)}%`,
      },
      {
        field: 'best_window_share_pct',
        headerName: 'Best-window %',
        type: 'number',
        minWidth: 120,
        flex: 0.9,
        align: 'right',
        headerAlign: 'right',
        renderHeader: () => (
          <HeaderWithTooltip
            label="Best-window %"
            tooltip="Share of this agent's dials placed within 1 hour of the lead state's best call hour."
          />
        ),
        valueFormatter: (value: number | null) => `${(value ?? 0).toFixed(1)}%`,
      },
      {
        field: 'appointment_calls',
        headerName: 'Appt calls',
        type: 'number',
        minWidth: 96,
        flex: 0.7,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | null) => (value ?? 0).toLocaleString(),
      },
      {
        field: 'appointment_kept_rate_pct',
        headerName: 'Kept %',
        type: 'number',
        minWidth: 88,
        flex: 0.7,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | null) => `${(value ?? 0).toFixed(1)}%`,
      },
      {
        field: 'cas_count',
        headerName: 'CAS',
        type: 'number',
        minWidth: 72,
        flex: 0.6,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number | null) => (value ?? 0).toLocaleString(),
      },
    ],
    [],
  );

  const gridSx = React.useMemo(
    () => buildDialOpGridSx(theme, { clickable }),
    [theme, clickable],
  );

  if (!rows.length) {
    const empty = (
      <Typography variant="body2" color="text.secondary" sx={bare ? { p: 2.5 } : undefined}>
        No agent activity in this period.
      </Typography>
    );
    if (bare) return empty;
    return (
      <Paper
        elevation={0}
        sx={(t) => ({ p: 3, borderRadius: `${t.ds.radius.md}px`, border: `1px solid ${t.tokens.border.subtle}` })}
      >
        {empty}
      </Paper>
    );
  }

  const grid = (
    <Box sx={{ width: '100%', minHeight: 360 }}>
      <DataGrid
        rows={gridRows}
        columns={columns}
        disableRowSelectionOnClick
        sortingMode="client"
        paginationMode="client"
        pageSizeOptions={DIAL_OP_PAGE_SIZE_OPTIONS}
        localeText={{ toolbarQuickFilterPlaceholder: 'Search agents…' }}
        initialState={{
          sorting: { sortModel: [{ field: 'dials', sort: 'desc' }] },
          pagination: { paginationModel: { pageSize: 10, page: 0 } },
        }}
        onRowClick={
          onAgentToggle
            ? (params: GridRowParams) => {
                const name = params.row.agent_full_nm as string | null;
                if (!name) return;
                onAgentToggle(name);
              }
            : undefined
        }
        getRowClassName={(params) =>
          selectedAgents?.includes(params.row.agent_full_nm) ? 'dialop-row-selected' : ''
        }
        slots={{ toolbar: DialOpGridToolbar }}
        sx={gridSx}
        autoHeight
      />
    </Box>
  );

  if (bare) return grid;

  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        overflow: 'hidden',
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
      })}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          By agent
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Descriptive only — not a performance ranking.
          {clickable
            ? ' Click an agent to add or remove them from the filter above, then hit Apply filters.'
            : ''}{' '}
          Search to find an agent.
        </Typography>
      </Box>
      {grid}
    </Paper>
  );
}
